import { Injectable, Injector } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { catchError, filter, finalize, map, shareReplay, tap, timeout } from 'rxjs/operators';
import { PreferenciasUsuarioService } from './preferencias-usuario.service';

export type PreferenciaTema = 'claro' | 'oscuro' | 'sistema';
type TemaResuelto = 'claro' | 'oscuro';

const CLAVE_STORAGE = 'wms-tema';
const CLAVE_PERMISO = 'wms-tema-habilitado';
// Tope de espera al backend en el login: si tarda más, el home entra en claro.
const ESPERA_BACKEND_MS = 5000;

const esPreferencia = (valor: unknown): valor is PreferenciaTema =>
  valor === 'claro' || valor === 'oscuro' || valor === 'sistema';

/**
 * Única fuente de verdad de qué rutas están adaptadas al modo oscuro: sólo WMS,
 * Compras y las páginas de inventarios ya migradas al diseño WMS. Fuera de aquí,
 * los componentes/overlays de Material no tienen estilos oscuros, así que el tema
 * oscuro NO debe aplicarse aunque la preferencia del usuario sea oscura.
 * `home.component.checarRuta()` reusa esta misma función para su clase `modulo-wms`.
 */
export const esRutaModuloOscuro = (url: string): boolean =>
  url.startsWith('/wms') ||
  url.startsWith('/compras') ||
  url.startsWith('/inventarios/recepcion-traspasos');

@Injectable({
  providedIn: 'root',
})
export class TemaService {
  private readonly consultaOscuro = window.matchMedia?.(
    '(prefers-color-scheme: dark)',
  );

  private permisoHabilitado: boolean =
    localStorage.getItem(CLAVE_PERMISO) === 'true';

  private temaBackend$: Observable<PreferenciaTema | null> | null = null;

  preferencia: PreferenciaTema = this.leerPreferencia();
  tema$ = new BehaviorSubject<TemaResuelto>(this.resolver(this.preferencia));

  // Ruta actual: decide si el tema oscuro se aplica (sólo en módulos adaptados).
  private rutaActual = '';

  constructor(private injector: Injector, private router: Router) {
    this.rutaActual = this.router.url;
    // El primer pintado no se anima: no hay tema previo del que hacer crossfade.
    this.aplicar(this.tema$.value, false);
    this.consultaOscuro?.addEventListener('change', () => {
      if (this.preferencia === 'sistema') this.actualizar();
    });
    // Al cambiar de módulo se re-evalúa el atributo: al salir de WMS/Compras con
    // preferencia oscura, el html vuelve a claro (y sus overlays con él); al entrar,
    // vuelve a oscuro. Sin animación para no chocar con la transición de ruta.
    this.router.events
      .pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd))
      .subscribe((e) => {
        this.rutaActual = e.urlAfterRedirects;
        this.aplicar(this.tema$.value, false);
      });
  }

  get puedeElegirTema(): boolean {
    return this.permisoHabilitado;
  }

  actualizarPermiso = (habilitado: boolean): void => {
    if (habilitado === this.permisoHabilitado) return;
    this.permisoHabilitado = habilitado;
    localStorage.setItem(CLAVE_PERMISO, String(habilitado));
    this.actualizar();
  };

  establecerPreferencia = (
    preferencia: PreferenciaTema,
    sincronizar = true,
    animar = true,
  ): void => {
    this.preferencia = preferencia;
    localStorage.setItem(CLAVE_STORAGE, preferencia);
    this.actualizar(animar);

    if (sincronizar) {
      this.injector
        .get(PreferenciasUsuarioService)
        .actualizarTema(preferencia)
        .subscribe({
          error: (err) => console.log('No se pudo sincronizar el tema con el backend', err),
        });
    }
  };

  /**
   * Tema guardado en el backend; `null` si la petición falla, tarda de más o
   * no trae un valor válido. Las llamadas concurrentes comparten una sola petición.
   */
  obtenerTemaBackend(): Observable<PreferenciaTema | null> {
    if (!this.temaBackend$) {
      this.temaBackend$ = this.injector
        .get(PreferenciasUsuarioService)
        .obtenerPreferencias()
        .pipe(
          timeout(ESPERA_BACKEND_MS),
          map((res) => {
            const tema = res?.payload?.tema;
            return esPreferencia(tema) ? tema : null;
          }),
          catchError((err) => {
            console.log('No se pudieron obtener las preferencias del usuario', err);
            return of(null);
          }),
          finalize(() => (this.temaBackend$ = null)),
          shareReplay(1),
        );
    }
    return this.temaBackend$;
  }

  /** Al iniciar sesión: deja aplicado el tema del backend (o claro) antes de entrar al home. */
  iniciarSesion(): Observable<void> {
    return this.obtenerTemaBackend().pipe(
      tap((tema) => this.establecerPreferencia(tema ?? 'claro', false, false)),
      map(() => undefined),
    );
  }

  private actualizar(animar = true): void {
    const resuelto = this.resolver(this.preferencia);
    // Cambio iniciado por el usuario (o por el sistema): crossfade suave.
    this.aplicar(resuelto, animar);
    this.tema$.next(resuelto);
  }

  private resolver(preferencia: PreferenciaTema): TemaResuelto {
    if (!this.permisoHabilitado) return 'claro';
    if (preferencia === 'sistema') {
      return this.consultaOscuro?.matches ? 'oscuro' : 'claro';
    }
    return preferencia;
  }

  private aplicar(tema: TemaResuelto, animar = false): void {
    // Dos señales en <html>:
    //  - data-tema = preferencia resuelta SIEMPRE. Gobierna el SHELL (navbar,
    //    sidenav, home/dashboard, menús de perfil/apps): quedan oscuros en toda
    //    la app cuando la preferencia es oscura.
    //  - data-tema-contenido = oscuro sólo si la preferencia es oscura Y la ruta
    //    es de un módulo adaptado. Gobierna el CONTENIDO de módulo y los overlays
    //    genéricos de Material (menús, diálogos, swals) que se renderizan a nivel
    //    body, fuera del subárbol .wms-forzar-claro. Así, en módulos no adaptados
    //    su contenido y modales se ven claros aunque el shell siga oscuro.
    const contenido: TemaResuelto =
      tema === 'oscuro' && esRutaModuloOscuro(this.rutaActual) ? 'oscuro' : 'claro';

    const escribir = () => {
      document.documentElement.setAttribute('data-tema', tema);
      document.documentElement.setAttribute('data-tema-contenido', contenido);
    };

    const iniciarTransicion = (
      document as unknown as {
        startViewTransition?: (cb: () => void) => unknown;
      }
    ).startViewTransition;

    const reduceMovimiento = window.matchMedia?.(
      '(prefers-reduced-motion: reduce)',
    ).matches;

    // View Transitions API: hace un crossfade del snapshot anterior al nuevo,
    // así el cambio de colores no se ve brusco. Sin soporte o con movimiento
    // reducido, se aplica directo (como antes).
    if (animar && iniciarTransicion && !reduceMovimiento) {
      iniciarTransicion.call(document, escribir);
    } else {
      escribir();
    }
  }

  private leerPreferencia(): PreferenciaTema {
    const guardada = localStorage.getItem(CLAVE_STORAGE);
    return esPreferencia(guardada) ? guardada : 'claro';
  }
}
